/**
 * The route table and the two layouts it hangs off.
 *
 * Every screen is loaded on demand, so a visitor who only signs in never
 * downloads the builder. Screens that need an account sit under a guard that
 * asks the backend once who is signed in; a refusal sends the visitor to
 * sign-in with the path they were after, so they land back on it afterwards
 * (contracts/app-api.md).
 *
 * @author John Grimes
 */

// This module's export is the route table itself, which is data rather than a
// component, so the fast-refresh rule cannot apply to it. The layouts and
// screens it points at are components in their own modules.
/* eslint-disable react-refresh/only-export-components */

import { useQuery } from "@tanstack/react-query";
import { Suspense, lazy, useEffect } from "react";
import {
  Link,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useRouteError,
} from "react-router";

import { AppErrorScreen } from "./components/AppErrorScreen";
import { AppNavbar } from "./components/AppNavbar";
import { ConnectionBanners } from "./components/ConnectionBanners";
import { DEFAULT_LANDING_PATH } from "./features/auth/authRules";
import { useSettings } from "./features/connections/useConnections";
import {
  apiJson,
  isApiError,
  subscribeToUnauthorised,
} from "./lib/api/apiClient";
import { useTheme } from "./lib/theme";

import type { JSX } from "react";
import type { Location, RouteObject } from "react-router";
import type { Me } from "shared/api";

// Route-level code splitting: each screen is a separate chunk, fetched when it
// is first routed to. The imports must be dynamic for the split to happen.
const SignInScreen = lazy(() =>
  import("./features/auth/SignInScreen").then((module) => ({
    default: module.SignInScreen,
  })),
);

const RegisterScreen = lazy(() =>
  import("./features/auth/RegisterScreen").then((module) => ({
    default: module.RegisterScreen,
  })),
);

const RecoveryCodesScreen = lazy(() =>
  import("./features/auth/RecoveryCodesScreen").then((module) => ({
    default: module.RecoveryCodesScreen,
  })),
);

const PasskeysScreen = lazy(() =>
  import("./features/auth/PasskeysScreen").then((module) => ({
    default: module.PasskeysScreen,
  })),
);

const ConnectionsScreen = lazy(() =>
  import("./features/connections/ConnectionsScreen").then((module) => ({
    default: module.ConnectionsScreen,
  })),
);

const AuthorisationCallbackScreen = lazy(() =>
  import("./features/connections/AuthorisationCallbackScreen").then(
    (module) => ({ default: module.AuthorisationCallbackScreen }),
  ),
);

const BrowseScreen = lazy(() =>
  import("./features/browse/BrowseScreen").then((module) => ({
    default: module.BrowseScreen,
  })),
);

const BuilderScreen = lazy(() =>
  import("./features/builder/BuilderScreen").then((module) => ({
    default: module.BuilderScreen,
  })),
);

const PipelinesScreen = lazy(() =>
  import("./features/pipelines/PipelinesScreen").then((module) => ({
    default: module.PipelinesScreen,
  })),
);

const PipelineScreen = lazy(() =>
  import("./features/pipelines/PipelineScreen").then((module) => ({
    default: module.PipelineScreen,
  })),
);

const SettingsScreen = lazy(() =>
  import("./features/settings/SettingsScreen").then((module) => ({
    default: module.SettingsScreen,
  })),
);

/** Props for {@link ShellStatus}. */
type ShellStatusProps = {
  /** What the app is doing, in plain language. */
  message: string;
};

/**
 * Says what the shell is waiting for, so no wait is ever a blank screen.
 *
 * @param props - The component's props.
 * @param props.message - What the app is doing.
 * @returns The status element.
 */
const ShellStatus = ({ message }: Readonly<ShellStatusProps>): JSX.Element => (
  <div role="status" className="flex-1 grid place-items-center p-8">
    <span className="flex items-center gap-2 text-sm opacity-70">
      <span className="loading loading-spinner loading-sm" aria-hidden />
      {message}
    </span>
  </div>
);

/**
 * The sign-in path that remembers where the visitor was headed.
 *
 * @param location - The location that could not be reached.
 * @returns The sign-in path, carrying the attempted path.
 */
const signInPathFor = (location: Location): string =>
  `/sign-in?next=${encodeURIComponent(`${location.pathname}${location.search}`)}`;

/**
 * Sends the visitor to sign-in whenever any request finds the session gone,
 * not just the session check itself.
 */
const useSignInOnUnauthorised = (): void => {
  const navigate = useNavigate();
  const location = useLocation();
  const signInPath = signInPathFor(location);
  useEffect(
    () =>
      subscribeToUnauthorised(() => {
        void navigate(signInPath, { replace: true });
      }),
    [navigate, signInPath],
  );
};

/**
 * Keeps the theme in step with the account's own setting, so a choice made in
 * one browser applies in the next one the user signs in to (FR-056). Mounted
 * only behind the guard, where the settings are already being read.
 *
 * This component renders nothing; it exists for its effect.
 */
const AccountTheme = (): undefined => {
  const { settings } = useSettings();
  const { setPreference } = useTheme();
  const preference = settings?.theme;
  useEffect(() => {
    if (preference !== undefined) setPreference(preference);
  }, [preference, setPreference]);
};

/**
 * Wraps the screens that need an account. It asks the backend who is signed in,
 * shows the navbar around the screen once it knows, and redirects to sign-in
 * when the answer is nobody.
 *
 * @returns The layout element, or a redirect to sign-in.
 * @throws {Error} Any failure other than an unauthorised one, for the app-level
 *   boundary to explain.
 */
const ProtectedLayout = (): JSX.Element => {
  const location = useLocation();
  useSignInOnUnauthorised();
  const { data, isPending, error } = useQuery({
    queryKey: ["me"],
    queryFn: () => apiJson<Me>("/me"),
    retry: false,
  });

  if (isPending) {
    return (
      <div className="min-h-screen flex flex-col bg-base-200 text-base-content">
        <ShellStatus message="Checking that you are signed in…" />
      </div>
    );
  }
  if (error) {
    if (isApiError(error) && error.unauthorised) {
      return <Navigate to={signInPathFor(location)} replace />;
    }
    throw error;
  }
  return (
    <div className="min-h-screen flex flex-col bg-base-200 text-base-content">
      <AppNavbar username={data.username} />
      <AccountTheme />
      <ConnectionBanners />
      <main className="flex-1 flex flex-col p-3 sm:p-6">
        <Suspense fallback={<ShellStatus message="Loading this screen…" />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
};

/**
 * Wraps the screens that must work without an account: signing in, registering
 * and an address that does not exist.
 *
 * @returns The layout element.
 */
const PublicLayout = (): JSX.Element => (
  <div className="min-h-screen flex flex-col bg-base-200 text-base-content">
    <AppNavbar />
    <main className="flex-1 grid place-items-center p-3 sm:p-6">
      <Suspense fallback={<ShellStatus message="Loading this screen…" />}>
        <Outlet />
      </Suspense>
    </main>
  </div>
);

/**
 * Explains an address the app does not serve, and offers the way back.
 *
 * @returns The not-found screen.
 */
const NotFoundScreen = (): JSX.Element => (
  <div className="card bg-base-100 border border-base-300 w-full max-w-md">
    <div className="card-body items-center text-center gap-3">
      <h1 className="text-lg font-semibold m-0">
        That page doesn&apos;t exist
      </h1>
      <p className="text-sm opacity-60 m-0 max-w-sm">
        The address may have changed, or the link that brought you here may be
        out of date.
      </p>
      <Link to="/connections" className="btn btn-primary mt-1">
        Go to your connections
      </Link>
    </div>
  </div>
);

/**
 * Catches anything thrown while a route renders. The router handles these
 * itself rather than letting them reach the application's error boundary, so
 * the routes need their own way of saying it plainly.
 *
 * @returns The error screen.
 */
const RouteErrorBoundary = (): JSX.Element => (
  <AppErrorScreen error={useRouteError()} />
);

/**
 * The application's routes. Exported as data so the shell can mount them in the
 * browser and the tests can mount them in memory.
 */
export const appRoutes: RouteObject[] = [
  {
    path: "/",
    element: <ProtectedLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <Navigate to={DEFAULT_LANDING_PATH} replace />,
      },
      { path: "connections", element: <ConnectionsScreen /> },
      // Where the browser lands after an authorisation round trip (FR-010).
      {
        path: "connections/callback",
        element: <AuthorisationCallbackScreen />,
      },
      { path: "recovery-codes", element: <RecoveryCodesScreen /> },
      { path: "passkeys", element: <PasskeysScreen /> },
      { path: "views", element: <BrowseScreen /> },
      { path: "views/new", element: <BuilderScreen /> },
      { path: "views/:viewId", element: <BuilderScreen /> },
      { path: "pipelines", element: <PipelinesScreen /> },
      { path: "pipelines/new", element: <PipelineScreen /> },
      { path: "pipelines/:pipelineId", element: <PipelineScreen /> },
      { path: "settings", element: <SettingsScreen /> },
    ],
  },
  {
    element: <PublicLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { path: "sign-in", element: <SignInScreen /> },
      { path: "register", element: <RegisterScreen /> },
      { path: "*", element: <NotFoundScreen /> },
    ],
  },
];
